import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { ChevronRight } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

import { fetchBaseDeclarada, fetchContraHoja, type PorMes } from "@/routes/finance/api"
import {
  AltaCrecimiento,
  AltaOportunidad,
  AltaPlanPersonas,
  AltaProducto,
} from "@/routes/finance/AltaDeclarado"
import { useFinanceFilters } from "@/routes/finance/context"

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
               "JUL", "AGO", "SET", "OCT", "NOV", "DIC"]

const M = Array.from({ length: 12 }, (_, i) => String(i + 1))

function suma(p: PorMes | undefined): number {
  if (!p) return 0
  return M.reduce((a, m) => a + (p[m] ?? 0), 0)
}

/**
 * EL FORECAST CONSTRUIDO DESDE LO DECLARADO.
 *
 * Cesar: «¿pero el forecast agarra de lo declarado en el maestro?». No lo hacía:
 * los meses futuros salían de la hoja de gestión importada y el módulo no leía
 * ni un contrato, teniendo 57 líneas cargadas. Firmar un contrato no movía la
 * proyección ni un dólar.
 *
 * ===========================================================================
 * LAS CAPAS NO SE SUMAN EN UN SOLO NÚMERO, Y ÉSA ES LA PANTALLA
 * ===========================================================================
 *
 * La pregunta que todo el mundo hace delante de un forecast es **«¿cuánto de
 * esto está cerrado?»**, y un total no la puede contestar: 100.000 firmados y
 * 100.000 apostados se ven exactamente igual. Así que lo primero que se lee aquí
 * es la **cobertura firmada** —qué parte de cada mes tiene un contrato detrás— y
 * sólo después el detalle.
 *
 * Con los datos de Siete va del 62% en julio al 21% en diciembre, que es
 * exactamente lo que tiene que pasar: cuanto más lejos se mira, menos hay
 * firmado. Una cobertura plana al 100% sería la señal de que algo está mal
 * contado.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ SE ENSEÑA AL LADO DE LA HOJA Y NO EN SU LUGAR
 *
 * Al calcularlo por primera vez apareció que lo contratado explicaba entre el
 * 14% y el 54% del ingreso de la hoja, y que la planilla declarada era cuatro a
 * seis veces la suya. Sustituir la fuente sin más habría hecho caer el ingreso
 * proyectado un 60% y multiplicar el costo por cinco: un forecast catastrófico,
 * bien formateado y falso.
 *
 * Resultó ser siembra y no un problema del negocio, pero la comparación se
 * queda: «¿cuánto de lo que proyecto está respaldado por algo que cargué?» hay
 * que poder contestarlo todos los meses.
 */
export function DeclaradoPage() {
  const { activeTenantId } = useAuth()
  const { anio, paisQuery } = useFinanceFilters()

  const base = useQuery({
    queryKey: ["forecast-declarado", activeTenantId, anio, paisQuery],
    queryFn: () => fetchBaseDeclarada(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const contra = useQuery({
    queryKey: ["forecast-contra-hoja", activeTenantId, anio, paisQuery],
    queryFn: () => fetchContraHoja(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })

  const d = base.data

  return (
    <div className="space-y-4">
      <WorkPanel
        title={t("Cómo se arma tu forecast")}
        meta={<span className="text-2xs text-muted-foreground">t("Capa por capa: contratos firmados, renovaciones, nuevo negocio y lo que declaraste. Importes en USD.")</span>}
      >
        <DataState
          loading={base.isLoading}
          error={base.error}
          empty={!d}
          emptyMessage={t("Todavía no hay nada declarado.")}
          emptyHint={t("Carga contratos, o declara nuevo negocio y crecimiento, y esta pantalla se llena sola.")}
        >
          {d ? (
            <>
              {/*
                LA COBERTURA VA ARRIBA DEL TODO, antes que ninguna cifra. Es lo
                que distingue un plan de un deseo, y leerla después de los
                totales sería leerla tarde.
              */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">{t("Capa")}</TableHead>
                    {MESES.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                    <TableHead className="text-right">{t("Total")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <Fila
                    etiqueta={t("Firmado")}
                    ayuda={t("Contratos vigentes y entregables comprometidos. Es un hecho con fecha.")}
                    datos={d.ingreso.firmado}
                    fuerte
                  />
                  <Fila
                    etiqueta={t("Apostado")}
                    ayuda={t("Renovaciones y nuevo negocio, ponderados por su probabilidad.")}
                    datos={d.ingreso.apostado}
                  />
                  <Fila
                    etiqueta={t("Declarado")}
                    ayuda={t("Plan de volumen e ingreso que declaraste a mano.")}
                    datos={d.ingreso.declarado}
                  />
                  <Fila etiqueta={t("Ventas proyectadas")} datos={d.ingreso.total} total />
                  <TableRow className="bg-surface-sunken">
                    <TableCell className="text-2xs uppercase tracking-[0.08em] text-muted-foreground">
                      {t("Cobertura firmada")}
                    </TableCell>
                    {M.map((m) => {
                      const v = d.ingreso.cobertura_firmada[m]
                      return (
                        <TableCell key={m} className="num text-right text-2xs">
                          {/*
                            `null` NO se pinta como 0%. Sin ingreso ese mes, un
                            0% se leería como «no tengo nada firmado» cuando lo
                            cierto es «no hay nada que cubrir».
                          */}
                          {v === null || v === undefined ? "—" : `${v}%`}
                        </TableCell>
                      )
                    })}
                    <TableCell className="num text-right text-2xs">
                      {d.ingreso.total && suma(d.ingreso.total) > 0
                        ? `${Math.round((100 * suma(d.ingreso.firmado)) / suma(d.ingreso.total))}%`
                        : "—"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/*
                EL SUPUESTO DE TIPO DE CAMBIO, con las cifras y no en una ayuda.
                Los tipos de cambio se acaban antes que el forecast —medido: el
                13/05/2026— así que estos importes llevan un supuesto dentro.
                Esconderlo haría la cifra incomprobable.
              */}
              {d.supuestos_fx ? (
                <p className="mt-3 rounded-md border border-warning/35 bg-warning-soft p-2.5 text-2xs leading-4">
                  {d.supuestos_fx}
                </p>
              ) : null}
            </>
          ) : null}
        </DataState>
      </WorkPanel>

      {/* ---------------------------------------------------------------- */}
      {d ? (
        <WorkPanel
          title={t("Ventas por cliente")}
          meta={<span className="text-2xs text-muted-foreground">t("Cada línea se abre para ver su contrato y sus entregables.")</span>}
        >
          <PorCliente base={d} />
        </WorkPanel>
      ) : null}

      {/*
        LOS PANELES SE DIBUJAN AUNQUE ESTÉN VACÍOS, y antes no: cada uno colgaba
        de `detalle.length > 0`. El efecto era el que Cesar describe en todo el
        traspaso —«no hay nada para subir»—: en un cliente sin nada declarado la
        pantalla no mostraba el panel, así que tampoco mostraba el botón para
        declarar el primero. Un panel que sólo aparece cuando ya tienes datos no
        sirve para empezar a tenerlos.
      */}
      {d ? (
        <WorkPanel
          title={t("Nuevo negocio")}
          meta={<span className="text-2xs text-muted-foreground">t("Lo que todavía no se cierra, ponderado por su probabilidad.")</span>}
          actions={<AltaOportunidad anio={anio} />}
        >
          {d.fuentes.oportunidades.detalle.length === 0 ? (
            <p className="px-2.5 py-2 text-2xs leading-5 text-muted-foreground">
              {t("No hay nuevo negocio declarado. Lo que declares aquí entra al forecast ponderado por su probabilidad, y se ve aparte de lo firmado.")}
            </p>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cliente")}</TableHead>
                <TableHead>{t("Línea")}</TableHead>
                <TableHead>{t("Etapa")}</TableHead>
                <TableHead className="text-right">{t("Mensual")}</TableHead>
                <TableHead className="text-right">{t("Probabilidad")}</TableHead>
                <TableHead className="text-right">{t("Ponderado")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.fuentes.oportunidades.detalle.map((o, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs font-medium">{o.cliente}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{o.linea ?? "—"}</TableCell>
                  <TableCell><Badge variant="brand-soft">{o.estado}</Badge></TableCell>
                  <TableCell className="num text-right">{formatAmount(o.monto_mensual_usd)}</TableCell>
                  <TableCell className="num text-right">{o.probabilidad}%</TableCell>
                  <TableCell className="num-strong text-right">
                    {formatAmount(o.ponderado_mensual_usd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </WorkPanel>
      ) : null}

      {d ? (
        <WorkPanel
          title={t("Venta por volumen")}
          meta={<span className="text-2xs text-muted-foreground">t("Unidades × precio, con el crecimiento compuesto mes a mes.")</span>}
          actions={<AltaProducto anio={anio} />}
        >
          {d.fuentes.producto.detalle.length === 0 ? (
            <p className="px-2.5 py-2 text-2xs leading-5 text-muted-foreground">
              {t("No hay productos declarados. Esto es para lo que se vende por unidades y no por contrato: cada SKU con sus unidades, su precio y su costo.")}
            </p>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Producto")}</TableHead>
                <TableHead className="text-right">{t("Unidades/mes")}</TableHead>
                <TableHead className="text-right">{t("Precio")}</TableHead>
                <TableHead className="text-right">{t("Costo")}</TableHead>
                {/*
                  EL MARGEN ES COLUMNA PROPIA Y NO SE DEDUCE. En consumo masivo
                  vender el doble con un costo que sube igual no mejora nada, y
                  obligar a restar dos columnas mentalmente es cómo nadie lo mira.
                */}
                <TableHead className="text-right">{t("Margen unitario")}</TableHead>
                <TableHead className="text-right">{t("Crecimiento")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.fuentes.producto.detalle.map((p, i) => (
                <TableRow key={i}>
                  <TableCell className="text-xs font-medium">
                    {p.producto}
                    {p.sku ? <span className="code ml-1.5 text-2xs text-muted-foreground">{p.sku}</span> : null}
                  </TableCell>
                  <TableCell className="num text-right">{formatAmount(p.unidades_mes)}</TableCell>
                  <TableCell className="num text-right">{formatAmount(p.precio_unitario_usd)}</TableCell>
                  <TableCell className="num text-right">
                    {p.costo_unitario_usd === null ? "—" : formatAmount(p.costo_unitario_usd)}
                  </TableCell>
                  <TableCell className="num-strong text-right">
                    {p.margen_unitario_usd === null ? "—" : formatAmount(p.margen_unitario_usd)}
                  </TableCell>
                  <TableCell className="num text-right">{p.crecimiento_mensual_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </WorkPanel>
      ) : null}

      {d ? (
        <WorkPanel
          title={t("Lo que va a crecer")}
          meta={<span className="text-2xs text-muted-foreground">t("Contrataciones previstas, aumentos y gasto declarado.")</span>}
          actions={
            <>
              <AltaPlanPersonas anio={anio} />
              <AltaCrecimiento anio={anio} />
            </>
          }
        >
          {d.fuentes.plan_personas.detalle.length === 0
           && d.fuentes.crecimiento.detalle.length === 0 ? (
            <p className="px-2.5 py-2 text-2xs leading-5 text-muted-foreground">
              {t("No hay nada declarado todavía. Una contratación prevista mueve el costo del mes en que entra; un gasto que crece, del mes en que empieza.")}
            </p>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Qué")}</TableHead>
                <TableHead>{t("Desde")}</TableHead>
                <TableHead className="text-right">{t("Mensual")}</TableHead>
                <TableHead>{t("Va a")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.fuentes.plan_personas.detalle.map((p, i) => (
                <TableRow key={`p${i}`}>
                  <TableCell className="text-xs font-medium">
                    {p.tipo === "aumento"
                      ? `${t("Aumento de sueldo")} ${p.porcentaje}%`
                      : `${p.cantidad} × ${p.puesto ?? t("sin puesto")}`}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {MESES[(p.meses[0] ?? 1) - 1]}
                  </TableCell>
                  <TableCell className="num text-right">
                    {p.costo_mensual_usd ? formatAmount(p.costo_mensual_usd) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.tipo === "aumento"
                      ? t("toda la planilla")
                      : p.es_facturable
                        ? t("Costo de ventas")
                        : t("Gasto operativo")}
                  </TableCell>
                </TableRow>
              ))}
              {d.fuentes.crecimiento.detalle.map((c, i) => (
                <TableRow key={`c${i}`}>
                  <TableCell className="text-xs font-medium">{c.concepto}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {MESES[(c.meses[0] ?? 1) - 1]}
                    {c.meses.length < 12 - (c.meses[0] ?? 1) + 1
                      ? ` – ${MESES[(c.meses[c.meses.length - 1] ?? 12) - 1]}`
                      : null}
                  </TableCell>
                  <TableCell className="num text-right">
                    {formatAmount(c.importe_mensual_usd)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.categoria}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </WorkPanel>
      ) : null}

      {/* ---------------------------------------------------------------- */}
      <WorkPanel
        title={t("Lo declarado contra tu hoja")}
        meta={<span className="text-2xs text-muted-foreground">t("Cuánto de lo que proyectas está respaldado por algo que ya cargaste.")</span>}
      >
        <DataState
          loading={contra.isLoading}
          error={contra.error}
          empty={!contra.data}
          emptyMessage={t("No hay con qué comparar.")}
        >
          {contra.data ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">{t("Ventas")}</TableHead>
                    {MESES.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <Fila etiqueta={t("Tu hoja")} datos={contra.data.ingreso.hoja} sinTotal />
                  <Fila etiqueta={t("Lo declarado")} datos={contra.data.ingreso.declarado} sinTotal />
                  <TableRow className="bg-surface-sunken">
                    <TableCell className="text-2xs uppercase tracking-[0.08em] text-muted-foreground">
                      {t("Cobertura")}
                    </TableCell>
                    {M.map((m) => {
                      const v = contra.data!.ingreso.cobertura_pct[m]
                      return (
                        <TableCell key={m} className="num text-right text-2xs">
                          {v === null || v === undefined ? "—" : `${v}%`}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableBody>
              </Table>
              <p className="mt-3 text-2xs leading-4 text-muted-foreground">
                {contra.data.nota}
              </p>
            </>
          ) : null}
        </DataState>
      </WorkPanel>
    </div>
  )
}

/** Una fila de doce meses más el total. */
function Fila({
  etiqueta,
  ayuda,
  datos,
  fuerte,
  total,
  sinTotal,
}: {
  etiqueta: string
  ayuda?: string
  datos: PorMes
  fuerte?: boolean
  total?: boolean
  sinTotal?: boolean
}) {
  return (
    <TableRow className={cn(total && "border-t-2 bg-surface-sunken")}>
      <TableCell className={cn("text-xs", (fuerte || total) && "font-medium")} title={ayuda}>
        {etiqueta}
      </TableCell>
      {M.map((m) => (
        <TableCell
          key={m}
          className={cn("text-right", total ? "num-strong" : "num",
                        (datos[m] ?? 0) === 0 && "num-zero")}
        >
          {formatAmount(datos[m] ?? 0)}
        </TableCell>
      ))}
      {sinTotal ? null : (
        <TableCell className={cn("text-right", total ? "num-strong" : "num")}>
          {formatAmount(suma(datos))}
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * EL CORTE POR CLIENTE, que es el que faltaba.
 *
 * Cesar, punto 7: «Revenue Build Up debería de tener todas las líneas de
 * clientes con su facturación y a qué hace referencia a nivel servicio, y
 * debería de poder ver el detalle; puede ser expandible cada línea».
 *
 * No estaba porque las cinco líneas del Revenue Build Up eran los `sub_account`
 * de la hoja importada —servicios, no clientes— y los 155 clientes de la base no
 * los leía nadie.
 *
 * LA RENOVACIÓN SE PINTA DENTRO DE LA FILA DEL CLIENTE y no como otro cliente:
 * es el mismo, con la diferencia de que esos meses dependen de que renueve. Dos
 * filas con el mismo nombre harían pensar que son dos cuentas.
 */
function PorCliente({ base }: { base: { fuentes: { contratos: any; hitos: any } } }) {
  const [abierto, setAbierto] = React.useState<string | null>(null)
  const clientes = base.fuentes.contratos.clientes as Array<{
    cliente: string; pais: string; linea: string | null
    meses: PorMes; meses_renovacion: PorMes
  }>
  const hitos = base.fuentes.hitos.detalle as Array<{
    cliente: string; descripcion: string; estado: string
    fecha_prevista: string; importe_usd: number
  }>

  if (clientes.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">
        {t("No hay contratos activos. Las ventas por cliente salen de ahí.")}
      </p>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-56">{t("Cliente")}</TableHead>
          <TableHead>{t("Servicio")}</TableHead>
          {MESES.map((m) => (
            <TableHead key={m} className="text-right">{m}</TableHead>
          ))}
          <TableHead className="text-right">{t("Total")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {clientes.map((c) => {
          const suyos = hitos.filter((h) => h.cliente === c.cliente)
          const renov = suma(c.meses_renovacion)
          const abierta = abierto === c.cliente
          const tieneDetalle = suyos.length > 0 || renov > 0
          return (
            <React.Fragment key={c.cliente}>
              <TableRow
                className={cn(tieneDetalle && "cursor-pointer hover:bg-accent")}
                onClick={() => tieneDetalle && setAbierto(abierta ? null : c.cliente)}
              >
                <TableCell className="text-xs font-medium">
                  <span className="flex items-center gap-1">
                    {tieneDetalle ? (
                      <ChevronRight
                        className={cn("h-3 w-3 shrink-0 transition-transform",
                                      abierta && "rotate-90")}
                        aria-hidden="true"
                      />
                    ) : (
                      <span className="w-3" />
                    )}
                    {c.cliente}
                    <span className="code text-2xs text-muted-foreground">{c.pais}</span>
                  </span>
                </TableCell>
                <TableCell className="truncate text-xs text-muted-foreground">
                  {c.linea ?? "—"}
                </TableCell>
                {M.map((m) => (
                  <TableCell
                    key={m}
                    className={cn("num text-right", (c.meses[m] ?? 0) === 0 && "num-zero")}
                  >
                    {formatAmount(c.meses[m] ?? 0)}
                  </TableCell>
                ))}
                <TableCell className="num-strong text-right">
                  {formatAmount(suma(c.meses))}
                </TableCell>
              </TableRow>

              {abierta ? (
                <TableRow className="bg-surface-sunken">
                  <TableCell colSpan={15} className="py-2">
                    <div className="space-y-1.5 pl-5">
                      {renov > 0 ? (
                        <p className="text-2xs leading-4 text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {formatAmount(renov)}
                          </span>{" "}
                          {t("del año dependen de que este contrato se renueve: están ponderados por su probabilidad, no dados por hechos.")}
                        </p>
                      ) : null}
                      {suyos.map((h, i) => (
                        <p key={i} className="flex items-center gap-2 text-2xs leading-4">
                          <Badge variant="brand-soft">{h.estado}</Badge>
                          <span className="font-medium">{h.descripcion}</span>
                          <span className="text-muted-foreground">{h.fecha_prevista}</span>
                          <span className="num ml-auto">{formatAmount(h.importe_usd)}</span>
                        </p>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
            </React.Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
